// @coreui/angular 2.11 の最小 vendored 版 (active-promotion.app 用)。
// digital-auction 由来のコンテナは <app-header> と sidebar toggler しか使わないため、
// その 2 つだけを CoreUI 2 の実装 (fesm2015 ソース) から移植した。
// スタイルは @coreui/coreui-pro 2.1.16 の SCSS (src/scss/style.scss 経由) をそのまま使う。
import {CommonModule, DOCUMENT} from '@angular/common';
import {
  Component,
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import {RouterModule} from '@angular/router';

// ---------------------------------------------------------------------------
// [appHtmlAttr]: オブジェクトの key/value を要素の属性として反映 (navbarBrand 用)
// ---------------------------------------------------------------------------
@Directive({selector: '[appHtmlAttr]', standalone: false})
export class HtmlAttributesDirective implements OnInit {
  @Input('appHtmlAttr') attributes: { [key: string]: string | number } | undefined;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    const attribs = this.attributes || {};
    for (const attr of Object.keys(attribs)) {
      if (attr === 'style' && typeof attribs[attr] === 'object') {
        continue;
      }
      this.renderer.setAttribute(this.el.nativeElement, attr, String(attribs[attr]));
    }
  }
}

// ---------------------------------------------------------------------------
// [appSidebarToggler]: body の sidebar-{bp}-show / sidebar-show クラスをトグル
// ---------------------------------------------------------------------------
@Directive({selector: '[appSidebarToggler]', standalone: false})
export class SidebarTogglerDirective {
  @Input('appSidebarToggler') breakpoint: string | boolean | undefined;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  @HostListener('click', ['$event'])
  toggleOpen(event: Event): void {
    event.preventDefault();
    const bp = typeof this.breakpoint === 'string' && this.breakpoint ? this.breakpoint : '';
    const cssClass = bp ? `sidebar-${bp}-show` : 'sidebar-show';
    this.document.body.classList.toggle(cssClass);
  }
}

// ---------------------------------------------------------------------------
// <app-header>: CoreUI 2 の AppHeaderComponent 相当 (aside toggler は省略)
// ---------------------------------------------------------------------------
@Component({
    standalone: false,
  selector: 'app-header, cui-header',
  host: {'[class.app-header]': 'true', '[class.navbar]': 'true'},
  template: `
    <ng-template [ngIf]="mobileSidebarToggler != false">
      <button class="navbar-toggler {{sidebarTogglerMobileClass}}" type="button" appSidebarToggler>
        <span class="navbar-toggler-icon"></span>
      </button>
    </ng-template>
    <a class="navbar-brand" [routerLink]="navbarBrandRouterLink">
      <img *ngIf="navbarBrand" [appHtmlAttr]="navbarBrand" class="navbar-brand">
      <img *ngIf="navbarBrandFull" [appHtmlAttr]="navbarBrandFull" class="navbar-brand-full">
      <img *ngIf="navbarBrandMinimized" [appHtmlAttr]="navbarBrandMinimized" class="navbar-brand-minimized">
    </a>
    <ng-template [ngIf]="sidebarToggler != false">
      <button class="navbar-toggler {{sidebarTogglerClass}}" type="button" [appSidebarToggler]="sidebarToggler">
        <span class="navbar-toggler-icon"></span>
      </button>
    </ng-template>
    <ng-content></ng-content>
  `,
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  @Input() fixed = false;
  @Input() navbarBrand: object | undefined;
  @Input() navbarBrandFull: object | undefined;
  @Input() navbarBrandMinimized: object | undefined;
  @Input() navbarBrandRouterLink: unknown[] | string = '';
  @Input() navbarBrandHref = '';
  @Input() sidebarToggler: string | boolean = 'md';
  @Input() mobileSidebarToggler: boolean = true;
  @Input() asideMenuToggler: string | boolean = false;
  @Input() mobileAsideMenuToggler: boolean = false;

  sidebarTogglerClass = 'd-none d-md-block';
  sidebarTogglerMobileClass = 'd-lg-none';
  private readonly breakpoints = ['xl', 'lg', 'md', 'sm', 'xs'];
  private readonly fixedClass = 'header-fixed';

  constructor(@Inject(DOCUMENT) private document: Document, private renderer: Renderer2) {}

  // ngOnInit で1度だけ計算するとオブジェクトリテラルの @Input (navbarBrandFull 等)が
  // 変更検知の都合で未設定のまま false 固定になり、ロゴ img が描画されないことがある。
  // getter にして毎回評価し、@Input 設定後に true になるようにする。
  get navbarBrandImg(): boolean {
    return Boolean(this.navbarBrand || this.navbarBrandFull || this.navbarBrandMinimized);
  }

  ngOnInit(): void {
    if (this.fixed) {
      this.renderer.addClass(this.document.body, this.fixedClass);
    }
    if (!(this.navbarBrandRouterLink as unknown[])[0]) {
      this.navbarBrandRouterLink = this.navbarBrandHref;
    }
    if (typeof this.sidebarToggler === 'string' && this.breakpoints.includes(this.sidebarToggler)) {
      this.sidebarTogglerClass = `d-none d-${this.sidebarToggler}-block`;
      this.sidebarTogglerMobileClass = `d-${this.sidebarToggler}-none`;
    }
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.body, this.fixedClass);
  }
}

// ---------------------------------------------------------------------------
// 旧 @coreui/angular のモジュール名互換 shim
// ---------------------------------------------------------------------------
@NgModule({
  // CommonModule が無いと NgIf が解決されず、テンプレートの *ngIf / [ngIf]
  // (ロゴ img・navbar-toggler) が一切描画されない。必ず import する。
  imports: [CommonModule, RouterModule],
  declarations: [AppHeaderComponent, HtmlAttributesDirective, SidebarTogglerDirective],
  exports: [AppHeaderComponent, HtmlAttributesDirective, SidebarTogglerDirective],
})
export class AppHeaderModule {}

@NgModule({})
export class AppAsideModule {}

@NgModule({})
export class AppFooterModule {}

@NgModule({})
export class AppSidebarModule {}

@NgModule({})
export class AppBreadcrumbModule {
  static forRoot() {
    return {ngModule: AppBreadcrumbModule, providers: []};
  }
}
